"use client";
import React from "react";
import { IBlocks, IConnections, IUserData } from "@/types";
import moment from "moment";
import { cn } from "@/lib/utils";
import {
    IconBrandTiktok,
    IconBrandTwitch,
    IconBrandTwitter,
    IconBrandYoutube,
    IconRosetteDiscountCheck,
} from "@tabler/icons-react";
import {
    Popover,
    PopoverTrigger,
    PopoverContent,
} from "@/components/ui/popover";
import { BlockUpdateFunction } from "./utils/blocks-map";
import AdminPopover from "./utils/AdminPopover";
import CountUpComponent from "./utils/Countup";
import {
    BORDER_RADIUS_OPTIONS,
    FONT_SIZE_OPTIONS,
    SHADOW_OPTIONS,
} from "./utils/block-style-classes";
import RenderIconOrAsset from "./utils/RenderIconOrAsset";

interface Props {
    edit?: boolean;
    account: IUserData;
    block: IBlocks;
    update?: BlockUpdateFunction;
    style: any;
    theme: IUserData["chosen_theme"];
    connections: {
        id: string;
        provider:
        | "twitch"
        | "youtube"
        | "twitter"
        | "bluesky"
        | "threads"
        | "instagram"
        | "tiktok"
        | "trovo";
        data: {
            [key: string]: any;
        };
    }[];
}

export default function BlockMediaKit({
    edit,
    account,
    block,
    update,
    style,
    theme,
    connections,
}: Props) {
    const [blockUpdate, setBlockUpdate] = React.useState(block);
    const activeConnections: IConnections[] = blockUpdate.options.data
        ? blockUpdate.options.data
            .map((data: any) =>
                connections.find((connection) => connection.id === data.id)
            )
            .filter(Boolean)
        : [];
    const totalFollowers = activeConnections.reduce((total, connection) => {
        if (connection.provider === "twitch" && connection.data?.followers) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "youtube" &&
            connection.data?.subscriberCount
        ) {
            return total + parseInt(connection.data.subscriberCount);
        } else if (
            connection.provider === "twitter" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "tiktok" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        } else if (
            connection.provider === "trovo" &&
            connection.data?.followers
        ) {
            return total + connection.data.followers;
        }
        return total;
    }, 0);
    // Add custom items followers/subscribers to total
    const customValues = blockUpdate.options.custom || [];
    let customTotal = 0;
    customValues.forEach((item: any) => {
        const mainValue = blockUpdate.metadata?.[item.totalKey || ""] || "0";
        if (mainValue) {
            customTotal += parseInt(mainValue) || 0;
        }
    });
    const totalWithCustom = totalFollowers + customTotal;
    // Calculate average updated_at timestamp
    const averageTimestamp =
        activeConnections.reduce((total, connection) => {
            return total + new Date(connection.updated_at).getTime();
        }, 0) / activeConnections.length;
    const averageDate = new Date(averageTimestamp);
    const formattedAverageDate = moment(averageDate).format("DD/MM/YY HH:mm");
    return (
        <AdminPopover
            edit={edit}
            className={block.show ? "" : "opacity-30"}
            title={`Media Kit`}
            block={block}
            update={update}
            setBlockUpdate={setBlockUpdate}
        >
            <div
                id="mediakit"
                style={style}
                className={cn(
                    "rounded-user bg-user-primary-color text-user-primary-text-color",
                    SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                    blockUpdate.style?.custom &&
                    BORDER_RADIUS_OPTIONS[blockUpdate.style.corner_radius]
                        ?.class,
                    blockUpdate.style?.custom &&
                    SHADOW_OPTIONS[blockUpdate.style.shadow]?.class,
                    "flex w-full flex-col gap-1 px-2 py-3 shadow-md transition-all",
                    blockUpdate.name.replaceAll(" ", "-")
                )}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1 text-lg font-bold">
                        {/* <p className="text-lg font-bold">Media Kit</p> */}
                    </div>
                    {blockUpdate.options?.total_followers && (
                        <div className="flex items-center gap-1 text-lg font-bold">
                            <p className="text-lg font-bold">
                                Total Followers:
                            </p>
                            <CountUpComponent
                                end={totalWithCustom}
                                admin={edit}
                            />
                        </div>
                    )}
                </div>
                <div>
                    {activeConnections.map((connection, index: number) => {
                        let new_style: React.CSSProperties = {
                            background: style.background,
                            color: style.color,
                        };
                        const options = blockUpdate.options.data.find(
                            (e: any) => e.id == connection.id
                        );
                        if (connection.provider == "twitch") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-twitch-${connection.data.username}`}
                                        href={`https://twitch.tv/${connection.data.username}`}
                                        className={cn(
                                            `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            `,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            BORDER_RADIUS_OPTIONS[
                                                blockUpdate.style
                                                    .corner_radius
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            SHADOW_OPTIONS[
                                                blockUpdate.style.shadow
                                            ]?.class
                                        )}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data.channel
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt="profile"
                                            />
                                            <IconBrandTwitch className="absolute bottom-0 right-0 rounded-tl-md bg-purple-500 p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                <p className="text-xl font-semibold">
                                                    <CountUpComponent
                                                        end={
                                                            connection.data
                                                                .followers
                                                        }
                                                        admin={edit}
                                                    />{" "}
                                                    Followers
                                                </p>
                                                {options.subscribers && (
                                                    <p>
                                                        Subscribers:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .subscriptions
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {(options.average_viewers ||
                                                    options.recent_followers ||
                                                    options.hours_watched) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 30 Days
                                                            </p>
                                                            {options.average_viewers && (
                                                                <p>
                                                                    Average Viewers:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .avg_viewers
                                                                    }
                                                                </p>
                                                            )}
                                                            {options.recent_followers && (
                                                                <p>
                                                                    Followers
                                                                    Gained:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .followers
                                                                    }
                                                                </p>
                                                            )}
                                                            {options.hours_watched && (
                                                                <p>
                                                                    Hours Watched:{" "}
                                                                    {
                                                                        connection
                                                                            .data
                                                                            .summary
                                                                            .hours_watched
                                                                    }
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "youtube") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-youtube-${connection.data.username}`}
                                        href={`https://youtube.com/${connection.data.username}`}
                                        className={cn(
                                            `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            `,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            BORDER_RADIUS_OPTIONS[
                                                blockUpdate.style
                                                    .corner_radius
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            SHADOW_OPTIONS[
                                                blockUpdate.style.shadow
                                            ]?.class
                                        )}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data.channel
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt={`${connection.data.username} YouTube Channel`}
                                            />
                                            <IconBrandYoutube className="absolute bottom-0 right-0 rounded-tl-md bg-red-500 p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                <p className="text-xl font-semibold">
                                                    <CountUpComponent
                                                        end={
                                                            connection.data
                                                                .subscriberCount
                                                        }
                                                        admin={edit}
                                                    />{" "}
                                                    Subscribers
                                                </p>
                                                {options.total_views && (
                                                    <span>
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .viewCount
                                                            }
                                                            admin={edit}
                                                        />{" "}
                                                        Views
                                                    </span>
                                                )}
                                                {(options.recent_views ||
                                                    options.viewer_approval ||
                                                    options.subscriber_change ||
                                                    options.average_watch_duration) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 30 Days
                                                            </p>
                                                            {options.recent_views && (
                                                                <p>
                                                                    Views:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.analytics
                                                                                ?.views ||
                                                                            0
                                                                        }
                                                                        admin={edit}
                                                                    />
                                                                </p>
                                                            )}
                                                            {options.viewer_approval && (
                                                                <p>
                                                                    Viewer Approval:{" "}
                                                                    {connection
                                                                        ?.data
                                                                        ?.analytics
                                                                        ?.likes &&
                                                                        connection?.data
                                                                            ?.analytics
                                                                            ?.dislikes
                                                                        ? (
                                                                            (connection
                                                                                .data
                                                                                .analytics
                                                                                .likes /
                                                                                (connection
                                                                                    .data
                                                                                    .analytics
                                                                                    .likes +
                                                                                    connection
                                                                                        .data
                                                                                        .analytics
                                                                                        .dislikes)) *
                                                                            100
                                                                        ).toFixed(
                                                                            1
                                                                        ) + "%"
                                                                        : "N/A"}
                                                                </p>
                                                            )}
                                                            {options.subscriber_change && (
                                                                <p>
                                                                    Subscriber
                                                                    Change:{" "}
                                                                    {(connection
                                                                        ?.data
                                                                        ?.analytics
                                                                        ?.subscribersGained ||
                                                                        0) -
                                                                        (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersLost ||
                                                                            0) >=
                                                                        0
                                                                        ? `+${(connection?.data?.analytics?.subscribersGained || 0) - (connection?.data?.analytics?.subscribersLost || 0)}`
                                                                        : (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersGained ||
                                                                            0) -
                                                                        (connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.subscribersLost ||
                                                                            0)}
                                                                </p>
                                                            )}
                                                            {options.average_watch_duration && (
                                                                <p>
                                                                    Average Watch
                                                                    Duration:{" "}
                                                                    {Number(
                                                                        connection
                                                                            ?.data
                                                                            ?.analytics
                                                                            ?.averageViewPercentage ||
                                                                        0
                                                                    ).toLocaleString()}
                                                                    %
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "twitter") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-twitter-${connection.data.username}`}
                                        href={`https://twitter.com/${connection.data.username}`}
                                        className={cn(
                                            `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            `,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            BORDER_RADIUS_OPTIONS[
                                                blockUpdate.style
                                                    .corner_radius
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            SHADOW_OPTIONS[
                                                blockUpdate.style.shadow
                                            ]?.class
                                        )}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                loading="lazy"
                                                alt="Profile Image"
                                            />
                                            <IconBrandTwitter className="absolute bottom-0 right-0 rounded-tl-md bg-[#3b82f6] p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={edit}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.following && (
                                                    <p>
                                                        Following:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .following
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {options.tweets && (
                                                    <p>
                                                        Tweets:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .tweets
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {options.likes && (
                                                    <p>
                                                        Likes:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .likes
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "tiktok") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-tiktok-${connection.data.username}`}
                                        href={`https://www.tiktok.com/@${connection.data.username}`}
                                        className={cn(
                                            `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            `,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            BORDER_RADIUS_OPTIONS[
                                                blockUpdate.style
                                                    .corner_radius
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            SHADOW_OPTIONS[
                                                blockUpdate.style.shadow
                                            ]?.class
                                        )}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data?.avatar ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt={`${connection.data.username} avatar`}
                                            />
                                            <IconBrandTiktok className="absolute bottom-0 right-0 rounded-tl-md bg-[#130b18] p-0.5 text-white" />
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={edit}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.following && (
                                                    <p>
                                                        Following:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .following
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {options.videos && (
                                                    <p>
                                                        Videos:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .videos
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {options.likes && (
                                                    <p>
                                                        Likes:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .likes
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                                {(options.recent_views ||
                                                    options.recent_likes) && (
                                                        <>
                                                            <p className="text-xl">
                                                                Last 20 videos
                                                            </p>
                                                            {options.recent_views && (
                                                                <p>
                                                                    Views:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.statistics
                                                                                ?.views ||
                                                                            0
                                                                        }
                                                                        admin={edit}
                                                                    />
                                                                </p>
                                                            )}
                                                            {options.recent_likes && (
                                                                <p>
                                                                    Likes:{" "}
                                                                    <CountUpComponent
                                                                        end={
                                                                            connection
                                                                                ?.data
                                                                                ?.statistics
                                                                                ?.likes ||
                                                                            0
                                                                        }
                                                                        admin={edit}
                                                                    />
                                                                </p>
                                                            )}
                                                        </>
                                                    )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        if (connection.provider == "trovo") {
                            if (options.theme) {
                                new_style.background =
                                    options.background_colour;
                                new_style.color = options.text_colour;
                            } else {
                                new_style = {
                                    background: style.background,
                                    color: style.color,
                                };
                            }
                            return (
                                <div
                                    key={index}
                                    className="flex flex-col gap-2 py-3 font-bold"
                                >
                                    <a
                                        target="_blank"
                                        id={`mediakit-trovo-${connection.data.username}`}
                                        href={`https://www.trovo.live/${connection.data.username}`}
                                        className={cn(
                                            `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105
                                            `,
                                            SHADOW_OPTIONS[
                                                theme.profile.design.shadow
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            BORDER_RADIUS_OPTIONS[
                                                blockUpdate.style
                                                    .corner_radius
                                            ]?.class,
                                            blockUpdate.style?.custom &&
                                            SHADOW_OPTIONS[
                                                blockUpdate.style.shadow
                                            ]?.class
                                        )}
                                        style={new_style}
                                    >
                                        <div className="relative shrink-0 overflow-hidden rounded-md">
                                            <img
                                                src={
                                                    connection.data
                                                        ?.profile_image ||
                                                    "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                }
                                                className={`h-24 w-24 shrink-0 rounded-full`}
                                                alt="Profile Image"
                                            />
                                            <img
                                                src="https://cdn.doras.to/doras/icons/light/trovo.png"
                                                className="absolute bottom-0 right-0 h-[24px] w-[24px] rounded-tl-md bg-[#34cb81] p-0.5 text-white"
                                                alt="Trovo"
                                            />{" "}
                                        </div>
                                        <div className="flex flex-col gap-2">
                                            <div className="flex w-full flex-wrap items-center gap-2">
                                                <p className="text-lg font-semibold">
                                                    {connection.data.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-1 px-1 text-base">
                                                {options.followers && (
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .followers
                                                            }
                                                            admin={edit}
                                                        />{" "}
                                                        Followers
                                                    </p>
                                                )}
                                                {options.subscribers && (
                                                    <p>
                                                        Subscribers:{" "}
                                                        <CountUpComponent
                                                            end={
                                                                connection.data
                                                                    .subscribers
                                                            }
                                                            admin={edit}
                                                        />
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </a>
                                </div>
                            );
                        }
                        return;
                    })}
                </div>
                {(blockUpdate.options.custom || []).length > 0 && (account.account_type === "premium" || account.account_type === "bplus" || account.account_type === "bpro") && (
                    <div>
                        {(blockUpdate.options.custom || []).map(
                            (item: any, index: number) => {
                                const metadataValue =
                                    blockUpdate.metadata?.[item.metadataKey] ||
                                    "0";
                                let new_style: React.CSSProperties = {
                                    background: style.background,
                                    color: style.color,
                                };
                                if (item.theme?.toggle) {
                                    new_style.background =
                                        item.theme.background_colour;
                                    new_style.color = item.theme.text_colour;
                                }
                                return (
                                    <div
                                        key={`custom-${index}`}
                                        className="flex flex-col gap-2 py-3 font-bold"
                                    >
                                        <div
                                            className={cn(
                                                `flex items-start gap-1 px-2 py-1 text-lg font-bold transition-all duration-300 hover:scale-105`,
                                                SHADOW_OPTIONS[
                                                    theme.profile.design.shadow
                                                ]?.class,
                                                blockUpdate.style?.custom &&
                                                BORDER_RADIUS_OPTIONS[
                                                    blockUpdate.style
                                                        .corner_radius
                                                ]?.class,
                                                blockUpdate.style?.custom &&
                                                SHADOW_OPTIONS[
                                                    blockUpdate.style.shadow
                                                ]?.class
                                            )}
                                            style={new_style}
                                            id={`mediakit-custom-${item.metadataKey}`}
                                        >
                                            <div className="relative shrink-0 overflow-hidden rounded-md">
                                                <img
                                                    src={
                                                        item.pictureUrl ||
                                                        "https://cdn.doras.to/doras/defaultimage.jpeg"
                                                    }
                                                    className="h-24 w-24 shrink-0 rounded-full"
                                                    alt={item.name}
                                                />

                                                <div className="absolute bottom-0 right-0 rounded-tl-md p-0.5" style={{
                                                    background: item.theme?.icon_background,
                                                    color: item.theme?.icon_colour,
                                                }} >
                                                    <RenderIconOrAsset account_id={account.id}
                                                        iconName={item.icon}
                                                        size={
                                                            (blockUpdate.style?.custom &&
                                                                FONT_SIZE_OPTIONS[
                                                                    blockUpdate.style.font_size
                                                                ]?.icon) ||
                                                            FONT_SIZE_OPTIONS[
                                                                theme.profile.design.font_size
                                                            ]?.icon ||
                                                            26
                                                        }
                                                        raw={true}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <div className="flex w-full flex-wrap items-center gap-2">
                                                    <p className="text-lg font-semibold">
                                                        {item.name}
                                                    </p>
                                                </div>
                                                <div className="flex flex-col gap-1 px-1 text-base">
                                                    <p className="text-xl font-semibold">
                                                        <CountUpComponent
                                                            end={
                                                                parseInt(
                                                                    metadataValue
                                                                ) || 0
                                                            }
                                                            admin={edit}
                                                        />{" "}
                                                        {item.label}
                                                    </p>
                                                    {(item.values || [])
                                                        .map(
                                                            (
                                                                valueItem: {
                                                                    label: string;
                                                                    metadataKey: string;
                                                                    position: "before" | "after";
                                                                },
                                                                valueIdx: number
                                                            ) => {
                                                                const value =
                                                                    blockUpdate
                                                                        .metadata?.[
                                                                    valueItem
                                                                        .metadataKey
                                                                    ] || "0";
                                                                if (valueItem.position === "before") {
                                                                    return (
                                                                        <p
                                                                            key={`${valueIdx}`}
                                                                        >
                                                                            {
                                                                                valueItem.label
                                                                            }{" "}
                                                                            <CountUpComponent
                                                                                end={
                                                                                    parseInt(
                                                                                        value
                                                                                    ) ||
                                                                                    0
                                                                                }
                                                                                admin={
                                                                                    edit
                                                                                }
                                                                            />
                                                                        </p>
                                                                    );
                                                                }
                                                                if (valueItem.position === "after") {
                                                                    return (
                                                                        <p
                                                                            key={`${valueIdx}`}
                                                                        >
                                                                            <CountUpComponent
                                                                                end={
                                                                                    parseInt(
                                                                                        value
                                                                                    ) ||
                                                                                    0
                                                                                }
                                                                                admin={
                                                                                    edit
                                                                                }
                                                                            />{" "}
                                                                            {
                                                                                valueItem.label
                                                                            }
                                                                        </p>
                                                                    );
                                                                }
                                                            }
                                                        )}
                                                    {item.updatedAtKey && (
                                                        <p className="text-xs text-muted-foreground">
                                                            Updated:{" "}
                                                            {moment(
                                                                blockUpdate
                                                                    .metadata?.[
                                                                item
                                                                    .updatedAtKey
                                                                ]
                                                            ).format(
                                                                "DD/MM/YY HH:mm"
                                                            )}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                        )}
                    </div>
                )}
                {/*  */}
                <Popover>
                    <PopoverTrigger asChild>
                        <div className="ml-auto flex cursor-pointer flex-col items-end gap-1">
                            <p className="flex items-center gap-1 text-sm">
                                <IconRosetteDiscountCheck size={18} /> Data
                                verified
                            </p>
                            {formattedAverageDate !== "Invalid date" && (
                                <p className="flex items-center gap-1 text-xs">
                                    Updated: {formattedAverageDate}
                                </p>
                            )}
                        </div>
                    </PopoverTrigger>
                    <PopoverContent
                        className={cn(
                            `flex w-full flex-col gap-3 rounded-user border-transparent bg-user-primary-color px-2 py-3 text-sm font-medium text-user-primary-text-color shadow-md`,
                            SHADOW_OPTIONS[theme.profile.design.shadow]?.class,
                            blockUpdate.style?.custom &&
                            BORDER_RADIUS_OPTIONS[
                                blockUpdate.style.corner_radius
                            ]?.class,
                            blockUpdate.style?.custom &&
                            SHADOW_OPTIONS[blockUpdate.style.shadow]?.class
                        )}
                    >
                        <p>
                            This data is verified by{" "}
                            <a
                                href={`https://doras.to${account.affiliate !== null ? "?aff=" + account.affiliate : ""}/`}
                                className="font-bold underline"
                            >
                                Doras.to
                            </a>
                            .
                        </p>
                    </PopoverContent>
                </Popover>
            </div>
        </AdminPopover>
    );
}
